import { createProgram, duplicateProgram, listPrograms, nextDay, type NextDay, type ProgramRow } from '@gymtv/data';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export function Programs() {
  const nav = useNavigate();
  const [programs, setPrograms] = useState<ProgramRow[] | null>(null);
  const [next, setNext] = useState<NextDay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [weeks, setWeeks] = useState('4');
  const [dayNames, setDayNames] = useState('Day A, Day B, Day C');
  const [busy, setBusy] = useState(false);

  const load = () => {
    listPrograms(supabase).then(setPrograms, (e) => setError(e.message));
    nextDay(supabase).then(setNext, () => setNext(null));
  };
  useEffect(load, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    const w = Number(weeks);
    const names = dayNames.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!name.trim()) return setError('Give the program a name.');
    if (!Number.isInteger(w) || w < 1 || w > 52) return setError('Weeks must be a whole number from 1 to 52.');
    if (names.length < 1 || names.length > 14) return setError('Add 1–14 training days, separated by commas.');
    setBusy(true);
    setError(null);
    try {
      const p = await createProgram(supabase, name.trim(), w, names);
      nav(`/programs/${p.id}`);
    } catch (err: any) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Programs</h1>
      </div>
      {next?.day_id && (
        <p className="notice">
          Next up in <b>{next.program_name}</b>: week {next.day_week}, <b>{next.day_name}</b> ({next.completed_days}/{next.total_days} sessions done). Start it from your phone.
        </p>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Program</th>
              <th>Weeks</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {programs?.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">No programs yet. Create one below.</td>
              </tr>
            )}
            {programs?.map((p) => (
              <tr key={p.id} className="link" onClick={() => nav(`/programs/${p.id}`)}>
                <td>
                  <b>{p.name}</b> {p.is_active && <span className="pill ok">active</span>}
                  {p.description && <div className="muted">{p.description}</div>}
                </td>
                <td className="num">{p.weeks}</td>
                <td className="num muted">{new Date(p.updated_at).toLocaleDateString()}</td>
                <td>
                  <button
                    className="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicateProgram(supabase, p.id).then(load, (err) => setError(err.message));
                    }}
                  >
                    Duplicate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form className="card stack" onSubmit={create} aria-label="New program">
        <h2>New program</h2>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <label className="field" style={{ flex: '2 1 220px' }}>
            Name
            <input id="new-program-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Winter strength block" />
          </label>
          <label className="field" style={{ flex: '0 0 90px' }}>
            Weeks
            <input id="new-program-weeks" inputMode="numeric" value={weeks} onChange={(e) => setWeeks(e.target.value)} />
          </label>
          <label className="field" style={{ flex: '3 1 260px' }}>
            Training days per week (comma separated)
            <input id="new-program-days" value={dayNames} onChange={(e) => setDayNames(e.target.value)} />
          </label>
          <button className="primary" type="submit" disabled={busy}>
            Create program
          </button>
        </div>
      </form>
    </>
  );
}
